import React, { useState, useContext, useEffect } from 'react';
import { authService } from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';

function AdminUserRegistrationForm() {
  const { user } = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    setRoles(['BUSINESS_DEVELOPMENT', 'SUPPLY_CHAIN']);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!username || !password || !email || !role) {
      setError('All fields are required.');
      return;
    }

    if (!email.endsWith('@eyantra.com')) {
      setError('Only @eyantra.com email addresses are allowed.');
      return;
    }

    setIsSubmitting(true);
    try {
      const registrationData = {
        username: username,
        password: password,
        email: email,
        role: role
      };
      await authService.post('/auth/register', registrationData);
      setMessage(`User ${username} created successfully! Please check your @eyantra.com email to verify your account.`);
      setUsername('');
      setPassword('');
      setEmail('');
      setRole('');
    } catch (err) {
      console.error('User creation failed:', err.response || err);
      setError(`Failed to create user: ${err.response?.data || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        eYantra EMS
      </Typography>
      <Typography variant="h5" component="h2" sx={{ color: 'text.secondary', mb: 2 }}>
        Register New User
      </Typography>
      {message && <Alert severity="success" sx={{ width: '100%', mt: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <TextField
          fullWidth
          margin="normal"
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          helperText="Must be an @eyantra.com email address"
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Role</InputLabel>
          <Select
            value={role}
            label="Role"
            onChange={(e) => setRole(e.target.value)}
          >
            {roles.map((r) => (
              <MenuItem key={r} value={r}>{r.replace(/_/g, ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit"/> : 'Register User'}
        </Button>
      </form>
       <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Already have an account?{' '}
          <MuiLink component={Link} to="/login" color="primary" sx={{fontWeight: 600}}>
            Login here
          </MuiLink>
        </Typography>
    </Box>
  );
}

export default AdminUserRegistrationForm;